import { animate, style, transition, trigger } from '@angular/animations';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Actions, ofActionDispatched, Select, Store } from '@ngxs/store';
import { isNil } from 'lodash';
import { combineLatest, merge, Observable, of, Subject } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  mergeMap,
  pairwise,
  skip,
  take,
  takeUntil,
} from 'rxjs/operators';
import { AuthUserType } from 'src/app/auth/models/auth-user-type.enum';
import { ContentViewsModel } from 'src/app/auth/models/content-views.model';
import {
  CheckContentViews,
  CheckContentViewsSuccess,
  ClearUserRestriction,
  Logout,
  NotifyUserRestriction,
} from 'src/app/auth/state/auth/auth.actions';
import { AuthState } from 'src/app/auth/state/auth/auth.state';
import { BookmarkActionsService } from 'src/app/bookmarks/bookmark-actions.service';
import { BookmarkLimitModel } from 'src/app/bookmarks/models/bookmark-limit.model';
import {
  CopyBookmarkSuccess,
  SaveBookmarkSuccess,
} from 'src/app/bookmarks/state/bookmarks.actions';
import { Breakpoint } from 'src/app/breakpoint/models/breakpoint.enum';
import { BreakpointState } from 'src/app/breakpoint/state/breakpoint.state';
import { DialogService } from 'src/app/dialogs/dialog.service';
import { DialogIds } from 'src/app/dialogs/models/dialog-ids';
import { OrganizationsState } from 'src/app/organizations/state/organizations.state';
import {
  LocalStorageEnum,
  SessionStorageEnum,
  StorageService,
} from 'src/app/shared/storage.service';
import { StoryChaptersState } from 'src/app/story/state/chapters/story.chapters.state';
import { CalloutContentModel } from '../../models/callout-content.model';
import { CalloutDisplayEnum } from '../../models/callout-display.enum';
import { UserRestrictionEnum } from '../../models/user-restriction.enum';
import { UserAccessService } from '../../user-access.service';

// # of model views thereshold
const viewMessageThreshold = 0.7;
// # of models saved thereshold
const saveMessageThreshold = 0.6;

// time to auto-dismiss footer notifications
const footerNotificationShowDelay = 2500;
const footerNotificationHideDelay = 20000;

interface NotificationModel {
  type: UserRestrictionEnum;
  display: CalloutDisplayEnum;
  data?: CalloutContentModel;
}

@Component({
  selector: 'app-user-access',
  templateUrl: './user-access.component.html',
  styleUrls: ['./user-access.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('inFromBottom', [
      transition(':enter', [
        style({
          transform: 'translateY(100%)',
        }),
        animate(
          '250ms ease-out',
          style({
            transform: '*',
          }),
        ),
      ]),
    ]),
  ],
})
export class UserAccessComponent implements OnInit, OnDestroy {
  breakPoints = Breakpoint;

  @Select(BreakpointState.getBreakpoint)
  breakpoint$: Observable<Breakpoint>;

  @Select(AuthState.getContentViews)
  contentViews$: Observable<ContentViewsModel>;

  displayTypes = CalloutDisplayEnum;
  gateTypes = UserRestrictionEnum;

  // notification types
  headerNotification: NotificationModel;
  footerNotification: NotificationModel;

  private ngUnsubscribe = new Subject();

  // utility states
  pageNavigation$: Observable<string>;

  constructor(
    private store: Store,
    private actions: Actions,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private userAccessService: UserAccessService,
    private bookmarkActionService: BookmarkActionsService,
    private storageService: StorageService,
    private dialogService: DialogService,
    private changeRef: ChangeDetectorRef,
  ) {
    // setup utilty states
    this.initUtilityStates();
  }

  ngOnInit(): void {
    // listen for notifications
    this.initNotificationListeners();

    // listen for user status changes
    this.initContentViews();
    this.initContentSaves();
    this.initFreeTrial();
    this.initPastDue();

    // setup content view checks
    this.initContentViewsCheck();
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  initContentViewsCheck() {
    // query content views when profile activated
    this.store
      .select(AuthState.getUserProfile)
      .pipe(
        map((userProfile) => !isNil(userProfile)),
        distinctUntilChanged(),
        filter((isProfileReady) => isProfileReady),
        takeUntil(this.ngUnsubscribe),
      )
      .subscribe(() => {
        this.store.dispatch(new CheckContentViews());
      });
  }

  initUtilityStates() {
    // page navigation
    this.pageNavigation$ = merge(
      this.activatedRoute.url,
      this.router.events.pipe(filter((e) => e instanceof NavigationEnd)),
    ).pipe(map(() => this.router.url));
  }

  initNotificationListeners() {
    // handle messages in header + footer
    this.actions
      .pipe(ofActionDispatched(NotifyUserRestriction), takeUntil(this.ngUnsubscribe))
      .subscribe((action: NotifyUserRestriction) => {
        const { type, display, data } = action;
        // handle specific types
        switch (display) {
          case CalloutDisplayEnum.Notification:
            this.headerNotification = action;
            break;
          case CalloutDisplayEnum.Footer:
            this.footerNotification = action;
            break;
        }
        this.changeRef.markForCheck();
      });

    // handle clear notifications
    this.actions
      .pipe(ofActionDispatched(ClearUserRestriction), takeUntil(this.ngUnsubscribe))
      .subscribe((action: ClearUserRestriction) => {
        action.displays.forEach((display) => {
          switch (display) {
            case CalloutDisplayEnum.Notification:
              this.headerNotification = undefined;
              break;
            case CalloutDisplayEnum.Footer:
              this.footerNotification = undefined;
              break;
          }
        });
        this.changeRef.markForCheck();
      });

    // clear notificaitons on navigation
    this.pageNavigation$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(() => {
      // close dialogs
      this.userAccessService.clearDialogs();

      // close notifications: disable header notfications on Studio
      const isStudio = this.isEditPage(this.router.url);
      this.clearNotifications(isStudio, true);
    });

    // clear notifications on log out
    this.actions.pipe(ofActionDispatched(Logout), takeUntil(this.ngUnsubscribe)).subscribe(() => {
      this.clearNotifications(true, true);
      this.changeRef.markForCheck();
    });
  }

  initContentViews() {
    // check navigation on view/edit pages
    const viewPageNavigation$ = this.pageNavigation$.pipe(
      map(() => this.isContentViewPage(this.router.url)),
      distinctUntilChanged((a, b) => a === b),
      filter((isViewNavigation) => isViewNavigation),
    );

    // last page
    let lastURL: string;
    this.pageNavigation$
      .pipe(
        map(() => this.router.url),
        pairwise(),
        takeUntil(this.ngUnsubscribe),
      )
      .subscribe(([lastNavUrl, currentNavUrl]) => {
        lastURL = lastNavUrl;
      });

    // limit reached
    const limitReached$ = this.contentViews$.pipe(
      map((contentViews: ContentViewsModel) => contentViews.limit_reached),
      distinctUntilChanged(),
    );

    // GATE: View Limit Reached
    viewPageNavigation$
      .pipe(
        // ignore premium users
        filter(() => !this.store.selectSnapshot(AuthState.isPremiumUser)),
        // get current content views
        mergeMap(() => limitReached$),
        // distinctUntilChanged((a, b) => a === b),
        // check if limit reached
        filter((limitReached) => limitReached),
        // show view gate
        mergeMap(() => this.userAccessService.openBookmarkViewLimit()),
        takeUntil(this.ngUnsubscribe),
      )
      .subscribe((redirect: boolean) => {
        // don't redirect on close
        if (!redirect) {
          return;
        }

        // ignore redirect when going to account/checkout
        const currentUrl = this.router.url;
        const isLastAccount = this.isAccountPage(lastURL);
        const isCurrentAccount = this.isAccountPage(currentUrl);
        if (isCurrentAccount) {
          return;
        }

        if (isNil(lastURL) || isLastAccount || this.isContentViewPage(lastURL)) {
          // prevent redirect look, back to dashboard
          this.router.navigate(['/results/bookmarks']);
        } else {
          // else last page
          this.router.navigateByUrl(lastURL);
        }
      });

    // GATE: View Limit Approaching

    // ready to query content views
    const contentViewReady$ = this.pageNavigation$.pipe(
      skip(1),
      mergeMap((url) => {
        const isContentViewPage = this.isContentViewPage(url);
        if (isContentViewPage) {
          // view/edit: wait for model ready
          return this.store
            .select(StoryChaptersState.getSelectedChapterReadyState)
            .pipe(filter((modelReady) => !isNil(modelReady) && modelReady.model));
        } else {
          // else ready to check
          return of(true);
        }
      }),
    );

    combineLatest([contentViewReady$, this.contentViews$])
      .pipe(
        distinctUntilChanged((lastContentViews, currentContentViews) => {
          const lastContentViewCount = lastContentViews[1].inapp_view_count;
          const currentContentViewCount = currentContentViews[1].inapp_view_count;
          return lastContentViewCount === currentContentViewCount;
        }),
        debounceTime(footerNotificationShowDelay),
        takeUntil(this.ngUnsubscribe),
      )
      .subscribe(([pageNavigation, contentViews]) => {
        const { inapp_view_count, inapp_view_limit } = contentViews;
        const currentViewThreshold = inapp_view_count / inapp_view_limit;
        const isAtThreshold =
          currentViewThreshold > viewMessageThreshold && currentViewThreshold < 1;

        // check: limit near threshold
        if (isAtThreshold) {
          this.userAccessService.openBookmarkViewWarning(inapp_view_count, inapp_view_limit);
          // todo: cleanup
          setTimeout(() => {
            this.userAccessService.clearUserNotifications(CalloutDisplayEnum.Footer);
          }, footerNotificationHideDelay);
        }
      });
  }

  initContentSaves() {
    // check save limit
    this.actions
      .pipe(
        ofActionDispatched(CopyBookmarkSuccess, SaveBookmarkSuccess),
        filter(() => !this.store.selectSnapshot(AuthState.isPremiumUser)),
        mergeMap(() => this.bookmarkActionService.checkBookmarkLimit()),
        debounceTime(footerNotificationShowDelay),
        takeUntil(this.ngUnsubscribe),
      )
      .subscribe((model: BookmarkLimitModel) => {
        const { inapp_content_limit, inapp_content_count } = model;
        const currentSaveThreshold = inapp_content_count / inapp_content_limit;
        const isAtThreshold =
          currentSaveThreshold > saveMessageThreshold && currentSaveThreshold < 1;

        if (isAtThreshold) {
          this.userAccessService.openBookmarkSaveWarning(inapp_content_count, inapp_content_limit);
          // todo: cleanup
          setTimeout(() => {
            this.userAccessService.clearUserNotifications(CalloutDisplayEnum.Footer);
          }, footerNotificationHideDelay);
        }
      });
  }

  initFreeTrial() {
    // check account status
    this.store
      .select(OrganizationsState.isTrialExpired)
      .pipe(
        filter((expired) => expired),
        filter(
          () =>
            this.storageService.getLocalItem(LocalStorageEnum.TeamTrialExpirationSeen) !== 'true',
        ),
        take(1),
        takeUntil(this.ngUnsubscribe),
      )
      .subscribe((expired) => {
        if (expired) {
          this.userAccessService.openTrialExpired();
        }
      });
  }

  initPastDue() {
    // check past due
    this.store
      .select(AuthState.getUserProfile)
      .pipe(
        filter((userProfile) => !isNil(userProfile)),
        filter(
          () => this.storageService.getLocalItem(LocalStorageEnum.ProExpirationSeen) !== 'true',
        ),
        takeUntil(this.ngUnsubscribe),
      )
      .subscribe((userProfile) => {
        const userType = this.store.selectSnapshot(AuthState.getUserType);
        if (userType !== AuthUserType.Team && userProfile.subscription) {
          const isAccountPastDue = userProfile.subscription.subscription_status === 'past due';
          if (isAccountPastDue) {
            this.userAccessService.openPastDue();
          }
        }
      });
  }

  handleActionClick(type: UserRestrictionEnum) {
    switch (type) {
      // forward to upgrade
      case UserRestrictionEnum.BookmarkViewThresholdReached:
      case UserRestrictionEnum.BookmarkSaveThresholdReached:
      case UserRestrictionEnum.BookmarkViewLimitReached:
      case UserRestrictionEnum.BookmarkSaveLimitReached:
        this.dialogService.openUpgradeDialog();
        break;
    }

    this.clearNotifications(false, true);
  }

  handleHeaderDismissClick(notification: NotificationModel) {
    // handle notification dismissed
    switch (notification.type) {
      case UserRestrictionEnum.ProAccountPastDue:
        this.storageService.setLocalItem(LocalStorageEnum.ProExpirationSeen, true);
        break;
      default:
        break;
    }

    this.clearNotifications(true, false);
    this.changeRef.markForCheck();
  }

  handleFooterDismissClick(notification: NotificationModel) {
    this.clearNotifications(false, true);
    this.changeRef.markForCheck();
  }

  private clearNotifications(clearHeader: boolean, clearFooter: boolean) {
    const displays: CalloutDisplayEnum[] = [];
    if (clearHeader) {
      displays.push(CalloutDisplayEnum.Notification);
      this.headerNotification = undefined;
    }
    if (clearFooter) {
      displays.push(CalloutDisplayEnum.Footer);
    }

    this.userAccessService.clearUserNotifications(...displays);
  }

  private isViewPage(url: string): boolean {
    return url.startsWith('/view');
  }

  private isEditPage(url: string): boolean {
    return url.startsWith('/edit');
  }

  private isAccountPage(url: string): boolean {
    return url.startsWith('/account');
  }

  private isContentViewPage(url: string): boolean {
    const isEdit = this.isEditPage(url);
    const isView = this.isViewPage(url);
    return isView || isEdit;
  }
}
